<template>
  <div data-cy="styleBackgroundSection">
    <div class="flex flex-row pb-2">
      <div class="flex-grow-2 text-xs font-bold text-secondary-invert flex items-center">
        {{ $t('frontend_builder_styles_background_section_color') }}
      </div>
      <div class="flex flex-grow pl-2">
        <div class="flex flex-grow justify-end">
          <color-picker
            v-model="properties.backgroundColor"
          ></color-picker>
        </div>
      </div>
    </div>

    <div class="pb-2">
      <span class="text-xs font-bold text-secondary-invert">{{$t('frontend_builder_styles_background_section_blur')}}</span>
      <gd-slider 
        :readonly="false"
        :min="0"
        :max="30"
        v-model="properties.backgroundBlur"
      />
    </div>

    <div class="layersystem-alert bg-secondary border border-secondary-light">
      <h5 class="text-primary">{{ this.$t('frontend_groovepages_builder_all_properties') }}</h5>
      <p class="text-secondary-invert" v-html="this.$t('frontend_groovepages_builder_layering_info_html')"></p>
      <div class="description">
        <div class="icon"><layer-system /></div>
        <div class="layers text-secondary-invert">
          <ul>
            <li>{{ this.$t('frontend_groovepages_builder_bg_color') }}</li>
            <li>{{ this.$t('frontend_groovepages_builder_bg_pattern') }}</li>
            <li class="hidden">{{ this.$t('frontend_groovepages_builder_bg_video') }}</li>
            <li>{{ this.$t('frontend_groovepages_builder_bg_image') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <radio v-model="currentBgOption" variant="tab" customCss :options="['Pattern','Image']"/>

    <div v-show="currentBgOption === 'Image'" class="bg-secondary px-1 rounded-b-lg" :class="{'rounded-t-lg' : !currentSelected.isBlock}">
      <image-picker
        v-model="properties.backgroundImage"
        class="px-2 bg-img-pk-text text-secondary-invert"
      ></image-picker>
      <div
        v-if="hasImage"
        class="flex flex-row px-2 pb-4"
      >
        <div class="text-xs font-bold text-secondary-invert flex justify-center mr-4 flex-col">
          {{ $t('frontend_builder_styles_background_section_repeat') }}
        </div>
        <gd-dropdown
          v-model="properties.backgroundRepeat"
          :options="availableBackgroundRepeat"
          :placeholder="$t('frontend_builder_background_section_repeat')"
          data-cy="backgroundRepeat"
          size="full"
          variant="dark"
        />
      </div>

      <div
        v-if="hasImage"
        class="flex flex-row pb-4 px-2"
        data-cy="backgroundSize"
      >
        <span class="text-xs font-bold text-secondary-invert mr-4 flex flex-col justify-center">{{
          $t('frontend_builder_styles_background_section_size')
        }}</span>
        <sides-panel
          v-model="properties.backgroundSize"
          :sides="{ auto: 'Auto', cover: 'Cover', contain: 'Contain' }"
        ></sides-panel>
      </div>
      <div
        v-if="hasImage"
        class="flex flex-row justify-between p-2"
        data-cy="backgroundPosition"
      >
        <span class="text-xs font-bold text-secondary-invert mr-2">{{
          $t('frontend_builder_styles_background_section_align')
        }}</span>
        <alignment-panel
          v-model="properties.backgroundPosition"
          :alignments="{
            center: '50%',
            bottom: 'bottom',
            top: 'top',
            left: '0',
            'left-bottom': '0 100%',
            'left-top': '0 0',
            right: '100%',
            'right-bottom': '100% 100%',
            'right-top': '100% 0'
          }"
        ></alignment-panel>
      </div>
    </div>

    <div v-show="currentBgOption === 'Pattern'" class="bg-secondary p-2 rounded-b-lg">

      <div class="flex flex-col flex-1 flex-grow mt-2" data-cy="bgPatternDropDown">
        <div class="text-xs text-secondary-invert pb-1">
              {{ this.$t('frontend_style_editor_pattern') }}
        </div>
        <gd-dropdown
          v-model="properties.backgroundPattern"
          :options="availablePatterns"
          variant="dark"
          size="full"
        />
      </div>

      <div v-if="properties.backgroundPattern && properties.backgroundPattern !== 'null'">
        <div class="flex flex-row pb-2 mt-3">
          <div class="flex-grow text-xs text-secondary-invert flex items-center">
            <span style="width:87px">{{ this.$t('frontend_style_editor_pattern_primary_color') }}</span>
          </div>
          <div class="flex flex-grow pl-2">
            <div class="flex flex-grow justify-end">
              <color-picker
                v-model="properties.backgroundPatternFrontColor"
              ></color-picker>
            </div>
          </div>
        </div>

        <div class="flex flex-row pb-2 mt-3">
          <div class="flex-grow text-xs text-secondary-invert flex items-center">
            <span style="width:87px">{{ this.$t('frontend_style_editor_pattern_secondary_color') }}</span>
          </div>
          <div class="flex flex-grow pl-2">
            <div class="flex flex-grow justify-end">
              <color-picker
                v-model="properties.backgroundPatternBackColor"
              ></color-picker>
            </div>
          </div>
        </div>

        <div>
          <div class="text-xs text-secondary-invert">
                {{ this.$t('frontend_style_editor_pattern_opacity') }}
          </div>
          <gd-slider
            v-model="properties.backgroundPatternOpacity"
            class="pb-2"
            size="full"
            :min="0"
            :max="100"
            :format-value="transparencyLabelValue"
            :readonly="false"
          />
        </div>

        <div>
          <div class="text-xs text-secondary-invert">
                {{ this.$t('frontend_style_editor_pattern_spacing') }}
          </div>
          <gd-slider
            v-model="properties.backgroundPatternSpacing"
            class="pb-2"
            size="full"
            :min="0"
            :max="100"
            :format-value="spacingLabelValue"
            :readonly="false"
          />
        </div>

        <div>
          <div class="text-xs text-secondary-invert">
                {{ this.$t('frontend_style_editor_pattern_background_color_opacity') }}
          </div>
          <gd-slider
            v-model="backgroundColorOpacity"
            class="pb-2"
            size="full"
            :min="0"
            :max="100"
            :readonly="false"
          />
          <text-panel v-show="backgroundColorOpacity > 90" :value="$t('frontend_style_editor_background_opacity_help')" />
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
  div[data-cy=styleBackgroundSection] {

    .bg-img-pk-text > div {
      font-size: 12px !important;
    }

    .radio-label {
      border: none;
      background: none;

      label {
        border: none;
        font-weight: bold;
        font-size: 12px;
        margin: 0 4px;
        border-radius: 0.5rem 0.5rem 0 0;

        &:first-child {
          margin-left: 0;
        }

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

.layersystem-alert {
    border-radius: 8px;
    margin: 15px 0 25px;
    padding: 7px 10px;

    > h5 {
        font-size: 17px!important;
        font-weight: 600;
    }

    > p {
        font-size: 13px;
        margin-top: 1px;
        line-height: 18px;
        margin-bottom: 11px;
        font-weight: 100;
    }

    > .description {
        display: flex;
        flex-direction: row;

        > .icon {flex: 0 0 32%;}

        > .layers {
            padding-left: 10px;
            font-size: 11px;
            font-weight: 100;
            line-height: 13px;
            position: relative;
            top: 8px;
        }
    }
}
</style>
<script>
import shortid from 'shortid';
import ColorPicker from '@groovepages/gd-ui-kit/components/ColorPicker';
import GdDropdown from '@groovepages/gd-ui-kit/components/Dropdown';
import GdCheckbox from '@groovepages/gd-ui-kit/components/Checkbox';
import GdToggle from '@groovepages/gd-ui-kit/components/Toggle';
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerInput';
import Radio from '@groovepages/gd-ui-kit/components/Radio';
import TextPanel from '@groovepages/gd-ui-kit/components/HtmlPanel';
import SidesPanel from '@groovepages/gd-ui-kit/components/SidesSelectorInput';
import AlignmentPanel from '@groovepages/gd-ui-kit/components/AlignmentInput';
import ConfigMenu from '@groovepages/gd-ui-kit/components/PluginMenu';
import GdSlider from '@groovepages/gd-ui-kit/components/Slider';

// import icons
import LayerSystem from '@/assets/images/icons/layer-system.svg';

export default {
  components: {
    LayerSystem,
    ColorPicker,
    ImagePicker,
    SidesPanel,
    GdDropdown,
    AlignmentPanel,
    GdCheckbox,
    GdToggle,
    ConfigMenu,
    Radio,
    TextPanel,
    GdSlider,
  },
  props: {
    styles: {
      required: true,
    },
  },
  data() {
    return {
      properties: this.styles,
      overlay: '',
      currentBgOption: 'Pattern',
      bblur: 0,
      patternOptions: {
        backColor: 'rgba(255, 255, 255, 1)',
        frontColor: 'rgba(0, 0, 0, 1)',
        pattern : 'null',
        spacing: 47,
        opacity: 20
      },
    };
  },
  mounted() {
    console.log('bgSection', this.styles);
  },
  created(){
    this.currentBgOption = "Image";
    return;
  },
  watch: {
    properties: {
      deep: true,
      handler(){
        this.$emit('change', this.properties);
      }
    }
  },
  computed: {
    backgroundSize: {
      get() {
        return 'cover';
      },
      set(value) {
        console.log('bgSize', value);
      },
    },
    backgroundPattern: {
      get() {
        return 'none';
      },
      set(value) {
        console.log('bgPattern', value);
      },
    },
    currentSelected() {
      return 'aa';
    },
    cascade() {
      return !this.$store.state.builder.singleScreenOverride;
    },
    sectionShouldShow() {
      if (this.overlay) {
        return this.show || hasValues(this.overlay.values);
      }
      return this.show;
    },
    hasImage() {
      return (
        this.properties.backgroundImage &&
        this.properties.backgroundImage != null
      );
    },
    componentConfigValues() {
      const {id} = this.videoBgComponent;
      return this.$store.getters.componentConfigs[id] || {};
    },
    resourcesPath() {
      return `${window.gdEnv.VUE_APP_STORAGE_URL}/plugins/`;
    },
    serverPath() {
      return `${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/call/`;
    },
    pluginSiteData() {
      return this.$store.getters.pluginSiteSettings;
    },
    backgroundColorOpacity: {
      get() {
        let value = this.getAlphaFromRGBA(this.properties.backgroundColor);
        value = value === null ? 100 : Math.floor(parseFloat(value)*100);
        return value;
      },
      set(value) {
        const [r,g,b,a] = this.getRGBAfromString(this.properties.backgroundColor);
        this.properties.backgroundColor = `rgba(${r},${g},${b},${value/100})`;
      }
    },
    availablePatterns(){
      return [
        { text: 'Select Pattern', value: 'null' },
        { text: 'Circles', value: 'Circles' },
        { text: 'Diagonal', value: 'Diagonal' },
        { text: 'Diagonalv2', value: 'Diagonalv2' },
        { text: 'Diagonalv3', value: 'Diagonalv3' },
        { text: 'Isometric', value: 'Isometric' },
        { text: 'Lines', value: 'Lines' },
        { text: 'Linesv2', value: 'Linesv2' },
        { text: 'Linesv3', value: 'Linesv3' },
        { text: 'Linesv4', value: 'Linesv4' },
        { text: 'Moon', value: 'Moon' },
        { text: 'Paper', value: 'Paper' },
        { text: 'Polka', value: 'Polka' },
        { text: 'Polkav2', value: 'Polkav2' },
        { text: 'Rectangle', value: 'Rectangle' },
        { text: 'Rhombus', value: 'Rhombus' },
        { text: 'Triangle', value: 'Triangle' },
        { text: 'Wavy', value: 'Wavy' },
        { text: 'ZigZag', value: 'ZigZag' },
      ];
    },
    availableBackgroundRepeat(){
      return [
        { text: 'No Repeat', value: 'no-repeat' },
        { text: 'Repeat', value: 'repeat' },
        { text: 'Repeat X', value: 'repeat-x' },
        { text: 'Repeat Y', value: 'repeat-y' },
      ];
    }
  },
  methods: { 
    getValue(id){
      return null;
    },
    setOverlayValues(data){
      console.log('overlayvalues',data);
    },
    updateComponent(data) {
      this.addComponentStylesheet();
      this.$store.commit('setModified', true);
      delete data.config.previousContent;
      const component = this.videoBgComponent;

      if (data.config.action === 'Block' || data.config.action === 'Popup') {
        data.config.new_tab = undefined;
      }
      this.$store.commit('storeComponentConfig', {
        id: component.id,
        value: data.config,
      });
      component.updateContent(data.value);
      if (component.getElement()) {
        this.parseComponentStyles([component.getElement()]);
      }
    },
    transparencyLabelValue(value) {
      const transparency = 100 - this.properties.opacity[value] * 100;
      return `${transparency  }%`;
    },
    spacingLabelValue(value){
      return `${value}%`;
    },
    sortArray(dataArr){
      return dataArr.sort((a,b) => {return a.text.localeCompare(b.text);});
    },
    getAlphaFromRGBA(rgba){
      if(!rgba || !rgba.includes('rgba')) return null;
      return `${rgba.replace(/^.*,(.+)\)/,'$1')}`;
    },
    getRGBAfromString(color){
      color = color.trim();
      if(color.includes('rgba')){
        color = color.replace('rgba','');
      }
      else {
        color = color.replace('rgb','');
      }
      color = color.replace('(','');
      color = color.replace(')','');
      color = color.split(',');
      const r = color[0];
      const g = color[1];
      const b = color[2];
      const a = color[3];
      return [r,g,b,a];
    }
  },
};
</script>
